<template>
  <div>
    <div v-if="displayTreeStructure && filder.type === 'title'" class="py-2 pl-12 text--small text--bold">
      {{ filder.display }} - {{ filder.title }}
    </div>
    <AppTooltip v-else :disabled="!isTooLargeToBeSigned">
      <template #activator="{ props }">
        <div class="AppClosdFildersTreeItem pl-6"
             :class="{'AppClosdFildersTreeItem-disabled': checkboxDisabled && !filder.unSelectable}"
             :data-locator="`${filder.type}-row`"
             :data-name="filder.basename"
             v-bind="props"
        >
          <div class="AppClosdFildersTreeItem-container">
            <v-icon v-if="folderHasChildren"
                    class="AppClosdFildersTreeItem-caretButton"
                    :class="{'AppClosdFildersTreeItem-caretButton--closed': !folderIsOpen, 'fa-spin': loadingChildren}"
                    @click="onCaretButtonClick"
            >
              {{ loadingChildren ? 'fas fa-spinner-third' : 'fas fa-caret-down' }}
            </v-icon>
          </div>
          <v-checkbox v-model="isChecked"
                      :indeterminate="partiallyChecked"
                      hide-details
                      :disabled="checkboxDisabled"
                      class="AppClosdFildersTreeItem-checkbox mt-0"
          >
            <template #label>
              <template v-if="filder.type === 'folder'">
                <font-awesome-icon v-if="folderIsOpen"
                                   class="AppClosdFildersTreeItem-checkboxLabelIcon AppClosdFildersTreeItem-checkboxLabelIcon--folder"
                                   :icon="['fad', 'folder-open']"
                                   size="lg"
                />
                <font-awesome-icon v-if="!folderIsOpen"
                                   class="AppClosdFildersTreeItem-checkboxLabelIcon AppClosdFildersTreeItem-checkboxLabelIcon--folder"
                                   :icon="['fad', 'folder']"
                                   size="lg"
                />
                {{ fullname }}
              </template>
              <template v-if="filder.type === 'file'">
                <ClosdFildersIcon class="AppClosdFildersTreeItem-checkboxLabelIcon" :document="filder"/>
                <span>{{ fullname }}</span>
                <app-badge v-if="filder.version > 1" class="ml-2">
                  {{ `v${filder.version}` }}
                </app-badge>
              </template>
            </template>
          </v-checkbox>
        </div>
      </template>
      <span>{{ $t('common.app-closd-filders-tree.AppClosdFildersTreeItem.isTooLargeToBeSigned', { size: maxSignableSize }) }}</span>
    </AppTooltip>
    <div v-show="folderIsOpen" class="pl-6">
      <AppClosdFildersTreeItem v-for="child in filder.children"
                               :key="`${child.type}-${child.id}`"
                               :display-tree-structure="displayTreeStructure"
                               :filder="child"
                               :foldersOnly="foldersOnly"
                               :selected-filders="selectedFilders"
                               :multiple="multiple"
                               :parent-is-selected="isChecked"
                               :disable-folder-without-file="disableFolderWithoutFile"
                               :disable-files-too-large-to-be-signed="disableFilesTooLargeToBeSigned"
                               :max-signable-size="maxSignableSize"
                               @child-checked="onChildCheck"
                               @check-children-except-one="checkChildrenExceptOne"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import { FOLDER_MAX_DEPTH } from '@/common/constants'
import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import { isFileLowerThanMaxSignableSize } from '@/common/utils/files'
import { numericSort } from '@/common/utils/sorting'
import documentsService from '@/services/documents.service'
import projectsService from '@/services/projectsFolders.service'

/**
 * Default Filder tree item
 * @displayName AppClosdFildersTreeItem
 */
/**
 * Display a folder or a file in AppClosdFildersTree
 * @see AppClosdFildersTree
 */
export default {
  name: 'AppClosdFildersTreeItem',
  components: { AppTooltip, ClosdFildersIcon },
  props: {
    /**
     * The item to display
     */
    filder: {
      type: Object,
      required: true,
    },
    /**
     * Displays tree titles and subtitles
     */
     displayTreeStructure: {
      type: Boolean,
      default: false,
    },
    /**
     * Array of selected items
     */
    selectedFilders: {
      type: Array,
      required: true,
    },
    /**
     * If the parent is selected. Internal purpose
     */
    parentIsSelected: {
      type: Boolean,
    },
    /**
     * Allow selecting multiple items
     */
    multiple: {
      type: Boolean,
      default: true,
    },
    /**
     * If true, display only files
     */
    filesOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * If true, disable checkbox of folders without files in children (deep)
     */
    disableFolderWithoutFile: {
      type: Boolean,
      required: true,
    },
    /**
     * If true, disable selection for files larger than the provided maxSignableSize
     */
    disableFilesTooLargeToBeSigned: {
      type: Boolean,
      default: false,
    },
    /**
     * If disableFilesTooLargeToBeSigned is true, this should be set
     */
    maxSignableSize: {
      type: [Number, String],
      default: 0,
    },
    /**
     * If true, display only folders
     */
    foldersOnly: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      folderIsOpen: false,
      loadingChildren: false,
    }
  },
  computed: {
    ...mapState('projectsFolders', ['roomToMove']),
    ...mapState('projectsFoldersMove', ['projectsFolderToMove']),
    isChecked: {
      get () { return this.indexIfSelected >= 0 || (this.parentIsSelected && this.multiple) },
      set (value) {
        if (value) {
          if (this.indexIfSelected < 0) {
            if (this.filesOnly && this.filder.type === 'folder') {
              return
            }
            if (!this.multiple) {
              this.selectedFilders.splice(0, this.selectedFilders.length)
              this.selectedFilders.push(this.filder)
            } else {
              this.selectedFilders.push(this.filder)
            }
            this.$emit('child-checked')
          }
        } else if (this.indexIfSelected >= 0) {
          this.selectedFilders.splice(this.indexIfSelected, 1)
        } else if (this.indexIfSelected < 0 && this.parentIsSelected) {
          this.$emit('check-children-except-one', this.filder)
        }
      },
    },
    partiallyChecked () {
      if (this.filder.type === 'folder') {
        return this.filder?.children?.some(o => this.selectedFilders.some(selectedFilder => selectedFilder.id === o.id && selectedFilder.type === o.type)) && this.multiple
      }
      return false
    },
    indexIfSelected () {
      return this.selectedFilders.findIndex(o => o.id === this.filder.id && o.type === this.filder.type)
    },
    fullname () {
      return (this.filder.numbering ? this.filder.numbering + ' ' : '') + (this.filder.basename || this.filder.name)
    },
    checkboxDisabled () {
      if (this.filder.locked || this.filder.unSelectable) {
        return true
      }
      if (this.filder.type === 'file') {
        return this.isTooLargeToBeSigned
      }

      if (!this.multiple && this.filesOnly) {
        return true
      }

      return this.disableFolderWithoutFile && !this.folderHasChildrenFile(this.filder)
    },
    isTooLargeToBeSigned () {
      if (this.filder.type === 'file' && this.disableFilesTooLargeToBeSigned) {
        return !isFileLowerThanMaxSignableSize(this.filder, this.maxSignableSize)
      }
      return false
    },
    folderHasChildren () {
      return this.filder.type === 'folder' && (this.filder.childrenCount > 0 || this.filder.children?.length > 0)
    },
  },
  watch: {
    parentIsSelected (value) {
      if (value) {
        if (this.indexIfSelected >= 0) {
          this.selectedFilders.splice(this.indexIfSelected, 1)
        }
      }
    },
  },
  mounted () {
    if (this.displayTreeStructure && this.filder.type === 'title') {
      this.folderIsOpen = true // Display title and subtitles by default
    }

    if (!this.displayTreeStructure && this.filder.children) {
      this.filder.children = this.filder.children.sort((a, b) => {
        if (a.type === 'folder' && b.type === 'file') {
          return 0
        } else if (a.type === 'file' && b.type === 'folder') {
          return 1
        } else {
          const aFullname = a.numbering ? `${a.numbering} ${a.basename}` : a.basename
          const bFullname = b.numbering ? `${b.numbering} ${b.basename}` : b.basename

          return numericSort(aFullname, bFullname)
        }
      })
    }
    if (this.foldersOnly && this.filder.children) {
      this.filder.children = this.filder.children.filter(child => child.type === 'folder')
    }
  },
  methods: {
    onChildCheck () {
      if (this.filder.type !== 'title' && this.filder.children.every(child => this.selectedFilders.some(o => o.id === child.id && o.type === child.type)) && this.multiple) {
        this.isChecked = true
        // If we don't return only the files, we can return the folder containg them all
        if (!this.filesOnly) {
          this.filder.children.forEach(f => {
            const index = this.selectedFilders.findIndex(o => o.id === f.id && o.type === f.type)
            if (index >= 0) {
              this.selectedFilders.splice(index, 1)
            }
          })
        }
      }
    },
    checkChildrenExceptOne (exceptFilder) {
      if (this.indexIfSelected >= 0) {
        this.selectedFilders.splice(this.indexIfSelected, 1)
      }
      this.filder.children.forEach(child => {
        if (child.id !== exceptFilder.id || child.type !== exceptFilder.type) {
          this.selectedFilders.push(child)
        }
      })
      if (this.parentIsSelected) {
        this.$emit('check-children-except-one', this.filder)
      }
    },
    onCaretButtonClick () {
      if (this.filder.locked) {
        return
      }
      if (this.displayTreeStructure || this.filder.childrenCount === this.filder?.children?.length) {
        this.folderIsOpen = !this.folderIsOpen
      } else if (this.filder.childrenCount > 0 && !this.folderIsOpen) {
        this.loadChildren()
      } else {
        this.folderIsOpen = !this.folderIsOpen
      }
    },
    async loadChildren () {
      this.loadingChildren = true
      try {
        if (this.$route.params.mnemo) {
          const response = await documentsService.getDocumentsById(this.$route.params.mnemo, this.filder.id)
          this.filder.children = response.data.children
            .filter(child => !this.foldersOnly || child.type === 'folder') // if foldersOnly, only show folders
            .map(child => ({
              ...child,
              key: `${child.type}-${child.id}`,
            }))
        } else {
          const response = await projectsService.getProjectsFolderById(this.filder.id)
          this.filder.children = response.data.children
            .map(folder => ({
              ...folder,
              type: 'folder',
              locked: (this.projectsFolderToMove.id && folder.depth === FOLDER_MAX_DEPTH) // Only locked when is a folder, because projects can be moved to any folder
                        || folder.rooms.map(item => item.id).includes(this.roomToMove.id), // Room cannot be moved to a folder that contains it
              key: `${folder.type}-${folder.id}`,
            }))
        }
        this.folderIsOpen = true
      } finally {
        this.loadingChildren = false
      }
    },
    folderHasChildrenFile (folder) {
      if (folder.children.some(filder => filder.type === 'file')) {
        return true
      }
      const childrenFolders = folder.children.filter(filder => filder.type === 'folder')
      return childrenFolders.some(this.folderHasChildrenFile)
    },
  },
}
</script>

<style scoped lang="scss">
.AppClosdFildersTreeItem {
  display: flex;
  align-items: center;
  min-height: 40px;
  overflow: hidden;
    &:hover, &:focus {
      background-color: #f7f7f7;
    }
}
.AppClosdFildersTreeItem-container {
  min-width: 24px;
}
.AppClosdFildersTreeItem-caretButton {
  width: 24px;
  height: 24px;
  &--closed {
    transform: rotate(-90deg);
  }
}
.AppClosdFildersTreeItem-checkbox {
  margin-left: 4px;
}
.AppClosdFildersTreeItem-checkboxLabelIcon {
  display: inline-block !important;
  vertical-align: middle;
}
.AppClosdFildersTreeItem-checkboxLabelIcon--folder {
  margin-right: 6px;
  --fa-primary-opacity: 1;
  --fa-secondary-opacity: 1;
  --fa-primary-color: #6aaefe;
  --fa-secondary-color: #6aaefe;
}
.AppClosdFildersTreeItem-disabled {
  opacity: 50%;
}
:deep(.AppClosdFildersTreeItem-disabled label) {
  color: #8D959D;
}
:deep(.AppClosdFildersTreeItem-checkbox) {
  label {
    white-space: nowrap;
    overflow: hidden;
    vertical-align: middle;
    text-overflow: ellipsis;
    padding-right: 50px;
  }
}
</style>
